const isNonEmptyObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value as Record<string, unknown>).length > 0;

export const normalizeRuntimeRecord = (
  row: Record<string, unknown>
): Record<string, unknown> => {
  const data =
    row && typeof row.data === 'object' && row.data
      ? (row.data as Record<string, unknown>)
      : {};
  const recordUuid = String(row.id || '').trim();
  const externalId = String(row.external_id || '').trim();
  const logicalId = String(data.id || '').trim() || externalId || recordUuid;
  return {
    ...data,
    // Keep logical id stable for app-level references after rehydration.
    id: logicalId,
    record_id: recordUuid,
    external_id: externalId || null,
    entity_type: row.entity_type,
    data,
  };
};

export const mergeTemplateStateWithRuntimeEntities = (
  state: Record<string, unknown> | null,
  entities: Record<string, Record<string, unknown>[]>
): Record<string, unknown> => {
  const base = isNonEmptyObject(state) ? state : {};
  const currentEntities = isNonEmptyObject(base.entities)
    ? (base.entities as Record<string, unknown>)
    : {};
  return {
    ...base,
    entities: {
      ...currentEntities,
      ...entities,
    },
  };
};

export interface DragRuntimeContextInput {
  entityType?: string | null;
  recordId?: string | null;
  externalId?: string | null;
  data?: Record<string, unknown> | null;
  dropStatus?: string | null;
  dropColumn?: string | null;
  orderIndex?: number | null;
}

export interface DragRuntimeUpdateAction {
  runtime_action: 'record.update';
  entity_type: string;
  record_id: string;
  external_id?: string;
  data: Record<string, unknown>;
}

export const inferDragRuntimeUpdateAction = (
  input: DragRuntimeContextInput
): DragRuntimeUpdateAction | null => {
  const entityType = String(input.entityType || '').trim();
  const resolvedRecordId =
    String(input.recordId || '').trim() || String(input.externalId || '').trim();
  if (!entityType || !resolvedRecordId) {
    return null;
  }
  const data = {
    ...(input.data || {}),
  } as Record<string, unknown>;
  if (input.dropStatus && data.status == null) {
    data.status = input.dropStatus;
  }
  if (input.dropColumn && data.column == null) {
    data.column = input.dropColumn;
  }
  if (typeof input.orderIndex === 'number' && Number.isFinite(input.orderIndex)) {
    data.order_index = input.orderIndex;
    if (data.position == null) {
      data.position = input.orderIndex;
    }
  }
  return {
    runtime_action: 'record.update',
    entity_type: entityType,
    record_id: resolvedRecordId,
    external_id: input.externalId ? String(input.externalId).trim() : undefined,
    data,
  };
};
